import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import Modal from '../../ui/Modal'
import Input from '../../ui/Input'
import Textarea from '../../ui/Textarea'
import Button from '../../ui/Button'
import ImageUpload from '../../ui/ImageUpload'
import PageSelector from './PageSelector'
import { useCurrentPage } from '../../../hooks/useCurrentPage'
import { ISSUE_TYPES } from '../../../lib/constants'
import { createIssue } from '../../../services/github'
import type { GitHubIssue } from '../../../types/github'

// Schéma de validation Zod
const ticketSchema = z.object({
  page: z.string().min(1, 'La page concernée est requise'),
  type: z.enum(['bug', 'enhancement'], {
    required_error: 'Le type est requis',
  }),
  title: z
    .string()
    .min(5, 'Le titre doit contenir au moins 5 caractères')
    .max(200, 'Le titre ne peut pas dépasser 200 caractères'),
  description: z
    .string()
    .min(10, 'La description doit contenir au moins 10 caractères')
    .max(2000, 'La description ne peut pas dépasser 2000 caractères'),
  attachmentUrl: z.string().url().optional().or(z.literal('')),
})

type TicketFormData = z.infer<typeof ticketSchema>

interface TicketFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: (issue: GitHubIssue) => void
}

function TicketFormModal({ isOpen, onClose, onSuccess }: TicketFormModalProps) {
  const currentPage = useCurrentPage()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<TicketFormData>({
    resolver: zodResolver(ticketSchema),
    defaultValues: {
      page: '',
      type: 'bug',
      title: '',
      description: '',
      attachmentUrl: '',
    },
  })

  // Pré-remplir le champ page avec la page actuelle
  useEffect(() => {
    if (isOpen && currentPage) {
      const pageValue = `${currentPage.name}|${currentPage.path}|${currentPage.label}`
      setValue('page', pageValue)
    }
  }, [isOpen, currentPage, setValue])

  // Reset form on close
  useEffect(() => {
    if (!isOpen) {
      reset()
    }
  }, [isOpen, reset])

  const attachmentUrl = watch('attachmentUrl')

  const onSubmit = async (data: TicketFormData) => {
    setIsSubmitting(true)

    try {
      // Parser le champ page pour extraire le label
      const pageParts = data.page.split('|')
      const pageLabel = pageParts[2] !== 'null' ? pageParts[2] : undefined

      const input = {
        title: data.title,
        body: data.description,
        type: data.type,
        labels: pageLabel ? [pageLabel] : undefined,
        page: data.page,
        attachmentUrl: data.attachmentUrl || undefined,
      }

      const issue = await createIssue(input)

      if (issue) {
        onClose()
        onSuccess?.(issue)
        reset()
      }
    } catch (error) {
      console.error('Error submitting ticket:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="p-6">
        <h2 className="text-2xl font-abandoned text-gray-900 mb-4">Créer un ticket</h2>
        <p className="text-sm text-gray-600 mb-6">
          Signalez un bug ou proposez une amélioration pour le site web
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Page concernée */}
          <PageSelector
            value={watch('page')}
            onChange={(value) => setValue('page', value)}
            required
            error={errors.page?.message}
          />

          {/* Type de ticket */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              {ISSUE_TYPES.map((issueType) => (
                <label
                  key={issueType.value}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    value={issueType.value}
                    {...register('type')}
                    className="w-4 h-4 text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-gray-700">{issueType.label}</span>
                </label>
              ))}
            </div>
            {errors.type && (
              <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
            )}
          </div>

          {/* Titre */}
          <Input
            label="Titre"
            placeholder="Résumé court du problème ou de l'amélioration"
            {...register('title')}
            error={errors.title?.message}
            required
          />

          {/* Description */}
          <Textarea
            label="Description"
            placeholder="Décrivez en détail le problème rencontré ou l'amélioration souhaitée..."
            rows={6}
            {...register('description')}
            error={errors.description?.message}
            required
          />

          {/* Pièce jointe */}
          <ImageUpload
            label="Pièce jointe (optionnel)"
            value={attachmentUrl || ''}
            onChange={(url) => setValue('attachmentUrl', url)}
            bucket="issue-attachments"
            folder=""
          />

          {/* Boutons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              Créer le ticket
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default TicketFormModal
